/**
 * 订单 API（order-receivable-management:ADM-09/ADM-10）。
 *
 * - POST   /orders          -> 从报名转订单 / 复购直建
 * - POST   /orders/direct   -> 管理端直建订单
 * - GET    /orders          -> 订单列表（筛选 + 分页 + 派生款项字段）
 * - GET    /orders/:id      -> 订单详情（订单行 / 报名覆盖 / 审计）
 * - POST   /orders/:id/cancel -> 取消待支付订单
 * - GET    /orders/:id/offline-payment-context -> 线下收款登记上下文
 * - POST   /orders/:id/offline-payments -> 线下收款登记
 * - POST   /orders/:id/offline-payment-receipts/upload-credentials -> 回单上传凭证
 * - POST   /orders/:id/offline-payment-receipts/access -> 回单服务端中转访问
 * - GET    /orders/:id/receivable-plans/:planId/dunning/recipients -> 催缴收件人（installment-dunning）
 * - POST   /orders/:id/receivable-plans/:planId/dunning -> 对指定微信号发送催缴
 */
import { ApiPaths } from './api.config';
import { http } from './transport';
import type { Paginated } from './campuses.api';

/** 订单来源。 */
export type OrderSource = 'PARENT_SELF' | 'STAFF_ASSISTED';

/** 订单状态。 */
export type OrderStatus =
  | 'PENDING_PAYMENT'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'CANCELLED'
  | 'EXPIRED';

/** 款项形态（首笔支付前未确定）。 */
export type OrderPaymentForm = 'UNDETERMINED' | 'FULL' | 'INSTALLMENT';

/** 应收节点类型。 */
export type ReceivableType = 'DEPOSIT' | 'FINAL' | 'FULL';

/** 应收节点到期状态。 */
export type ReceivableDueStatus = 'PENDING' | 'OVERDUE' | 'SETTLED';

/** 员工 / 管理端切换商品的结构化原因。 */
export type OrderSwitchReasonCode =
  | 'SALES_ADJUSTMENT'
  | 'PARENT_REQUEST'
  | 'SCHEDULE_CONFLICT'
  | 'PRICE_PLAN_CHANGE'
  | 'OTHER';

/** 订单冻结的商品快照。 */
export interface OrderProductSnapshot {
  productId: string;
  name: string;
  coverImage: string | null;
  provinceName: string;
  cityName: string;
  features: string[];
  price: number;
  depositRatio: number;
  finalDueDays: number;
  skus: Array<{ skuId: string; skuName: string; lessonCount: number }>;
}

/** 订单行（一个 SKU 一行，整包购买 quantity=1）。 */
export interface OrderItem {
  orderItemId: string;
  skuId: string;
  skuSnapshot: {
    skuId: string;
    skuName: string;
    unitPrice: number;
    lessonCount?: number;
  } & Record<string, unknown>;
  quantity: number;
  unitPrice: number;
  amount: number;
}

/** 应收节点（首笔支付后物化）。 */
export interface ReceivablePlan {
  receivablePlanId: string;
  type: ReceivableType;
  amount: number;
  dueAt: string | null;
  paidAmount: number;
  status: 'PENDING' | 'SETTLED' | 'CANCELLED';
  overdue?: boolean;
}

/** SKU 收入分摊事实。 */
export interface OrderSkuRevenueAllocation {
  allocationId: string;
  orderItemId: string;
  skuId: string;
  theoreticalAmount: number;
  allocationRatio: number;
  allocatedReceivable: number;
  strategyKey: string;
  strategyVersion: number;
}

/** 订单列表 / 详情基础视图。 */
export interface OrderListItem {
  orderId: string;
  orderNo: string;
  sourceSubmissionId: string | null;
  studentId: string;
  studentName: string;
  guardianMobile: string | null;
  campusId: string;
  campusName: string;
  productId: string;
  productSnapshot: OrderProductSnapshot;
  originalAmount: number;
  discountAmount: number;
  receivableAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  /** 累计退款金额（分，order-manual-refund） */
  refundedAmount: number;
  /** 实收口径（Σ活跃节点已收 − 累计退款，分；五处展示点同源此值） */
  netReceivedAmount: number;
  paymentForm: OrderPaymentForm;
  currentReceivablePlan: ReceivablePlan | null;
  status: OrderStatus;
  source: OrderSource;
  agreementVersion: string | null;
  agreedAt: string | null;
  expiresAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  items: OrderItem[];
  receivablePlans: ReceivablePlan[];
  allocations: OrderSkuRevenueAllocation[];
}

/** 详情报名覆盖视图。 */
export interface LinkedApplication {
  applicationId: string;
  skuId: string;
  skuName: string;
  status: string;
}

/** 详情未试听 / 剩余跟进视图。 */
export interface UncoveredSku {
  applicationId?: string;
  skuId: string;
  skuName: string;
}

/** 退款记录视图（两源合并：人工登记 + 差错退款；原型 ADM-10「退款记录」Tab） */
export interface OrderRefundRecord {
  refundNo: string;
  source: 'MANUAL' | 'FUND_EXCEPTION';
  channel: string | null;
  channelRefNo: string | null;
  amountMinor: number;
  status: string;
  operatorName: string | null;
  refundedAt: string;
  reason: string | null;
}

/** billing 审计日志视图。 */
export interface OrderAuditLog {
  auditLogId: string;
  operatorId: string;
  /** 操作人姓名（operator_name 快照兜底 JOIN staffs）。 */
  operatorName: string | null;
  operatedAt: string;
  action: string;
  result: string;
  detail: Record<string, unknown> | null;
  /** 业务原因专列（6-5） */
  reason: string | null;
  /** 变更前值（6-5 专列，JSON 文本） */
  beforeValue: string | null;
  /** 变更后值（6-5 专列，JSON 文本） */
  afterValue: string | null;
}

/** 订单详情支付流水摘要（按支付时间倒序）。 */
export interface OrderPaymentTransaction {
  paymentTransactionId: string;
  businessPaymentNo: string;
  channel: string;
  channelTxnNo: string | null;
  amountMinor: number;
  status: string;
  paidAt: string;
  operator: { staffId: string | null; name: string | null };
  receivablePlanId: string;
  receiptFileKey: string | null;
  remark: string | null;
}

/** 订单详情视图。 */
export interface OrderDetail extends OrderListItem {
  sourceProductId: string | null;
  linkedApplications: LinkedApplication[];
  untrialledSkus: UncoveredSku[];
  remainingUncoveredApplications: UncoveredSku[];
  paymentTransactions: OrderPaymentTransaction[];
  auditLogs: OrderAuditLog[];
  /** 退款记录（两源合并，原型 ADM-10「退款记录」Tab） */
  refunds: OrderRefundRecord[];
}

/** 创建订单结果。 */
export interface CreateOrderResult {
  orderId: string;
  orderNo: string;
  sourceSubmissionId: string | null;
  remainingUncoveredApplicationCount: number;
  remainingUncoveredApplications: UncoveredSku[];
  idempotentReused: boolean;
}

/** 订单列表查询参数。 */
export interface OrderListQuery {
  orderNo?: string;
  /** 综合检索：订单号 / 学生姓名 / 监护人手机号。 */
  keyword?: string;
  studentId?: string;
  guardianMobile?: string;
  campusId?: string;
  productId?: string;
  skuId?: string;
  sourceSubmissionId?: string;
  source?: OrderSource;
  status?: OrderStatus;
  receivableType?: ReceivableType;
  dueStatus?: ReceivableDueStatus;
  page?: number;
  pageSize?: number;
  /** 订单创建日起 YYYY-MM-DD（校区时区，含当日）。 */
  createdFrom?: string;
  /** 订单创建日止 YYYY-MM-DD（校区时区，含当日）。 */
  createdTo?: string;
  /** 排除的订单状态（序列化为逗号分隔）。 */
  excludeStatuses?: OrderStatus[];
}

/** 从报名转订单 / 复购直建载荷。 */
export interface CreateOrderPayload {
  sourceSubmissionId?: string | null;
  studentId: string;
  campusId: string;
  productId: string;
  source: OrderSource;
  agreementAccepted?: boolean;
  switchConfirmed?: boolean;
  switchReasonCode?: OrderSwitchReasonCode | null;
  switchReasonNote?: string | null;
  expiresAt?: string | null;
}

/** 管理端直建载荷。 */
export interface CreateDirectOrderPayload {
  student: { studentId: string; campusId: string };
  productId: string;
  expiresAt?: string | null;
  /** 订单级减免金额（整数分，≥0；需 ADM-09 DISCOUNT 动作，未授权不得携带）。 */
  discountAmount?: number | null;
  /** 减免原因（减免大于 0 时必填，≤200 字）。 */
  discountReason?: string | null;
}

/** 线下收款登记目标；金额只能来自服务端返回的选项。 */
export type OfflinePaymentTargetCode =
  | 'FULL'
  | 'INSTALLMENT'
  | 'DEPOSIT'
  | 'FINAL';

/** 线下收款渠道。 */
export type OfflinePaymentChannelCode =
  | 'WECHAT'
  | 'ALIPAY'
  | 'CASH'
  | 'BANK_TRANSFER'
  | 'OTHER';

/** 线下收款登记中的员工摘要。 */
export interface OfflinePaymentStaff {
  staffId: string;
  name: string;
}

/** 线下收款登记中的学生摘要。 */
export interface OfflinePaymentStudent {
  studentId: string;
  name: string;
  status: string;
}

/** 线下收款登记中的校区摘要。 */
export interface OfflinePaymentCampus {
  campusId: string;
  name: string;
  timezone: string;
}

/** 服务端计算出的可登记金额选项。 */
export interface OfflinePaymentOption {
  target: OfflinePaymentTargetCode;
  receivablePlanId: string | null;
  amountMinor: number;
  paidAmountMinor: number;
  payableAmountMinor: number;
  dueAt: string | null;
  followUpPlan: {
    target: 'FINAL';
    amountMinor: number;
  } | null;
}

/** 线下收款登记上下文。 */
export interface OfflinePaymentContext {
  orderId: string;
  orderNo: string;
  orderStatus: OrderStatus;
  paymentForm: OrderPaymentForm;
  receivableAmountMinor: number;
  paidAmountMinor: number;
  outstandingAmountMinor: number;
  expiresAt: string | null;
  student: OfflinePaymentStudent;
  campus: OfflinePaymentCampus;
  productSnapshot: OrderProductSnapshot;
  currentStaff: OfflinePaymentStaff | null;
  payable: boolean;
  notPayableReason: string | null;
  options: OfflinePaymentOption[];
}

/** 线下收款登记请求；契约禁止提交任何金额字段。 */
export interface RecordOfflinePaymentPayload {
  intendedPaymentForm?: 'FULL' | 'INSTALLMENT';
  receivablePlanId?: string;
  channel: OfflinePaymentChannelCode;
  channelEventAt: string;
  channelTxnNo?: string | null;
  receiptObjectKey?: string | null;
  remark?: string | null;
}

/** 线下收款登记结果。 */
export interface RecordOfflinePaymentResult {
  rawEventId: string;
  businessPaymentNo: string;
  paymentTransactionId: string | null;
  receivablePlanId: string | null;
  orderStatus: OrderStatus;
  settlementStage: string;
  idempotentReused: boolean;
}

/** 线下回单短时上传凭证。 */
export interface OfflinePaymentReceiptUploadCredentials {
  secretId: string;
  secretKey: string;
  sessionToken: string;
  expiredAt: string;
  allowedPrefix: string;
  /** 直传目标桶/地域（服务端凭证下发，real 模式直传必需） */
  bucket?: string;
  region?: string;
}

/** 线下回单上传凭证签发结果。 */
export interface RequestOfflinePaymentReceiptUploadPayload {
  filename: string;
  fileSizeBytes: number;
}

export interface RequestOfflinePaymentReceiptUploadResult {
  objectKey: string;
  credentials: OfflinePaymentReceiptUploadCredentials;
  directUpload: boolean;
  filename: string;
}

/** 线下回单服务端中转访问请求与响应。 */
export interface AccessOfflinePaymentReceiptPayload {
  objectKey: string;
}

export interface AccessOfflinePaymentReceiptResult {
  mode: 'server-relay';
  objectKey: string;
  filename: string;
  contentType: string;
  contentBase64: string;
}

/** 单条催缴收件人视图（一个微信号一条，员工逐条选择发送）。 */
export interface DunningRecipientView {
  /** 家长微信身份记录 ID（发送命令以此定位收件人）。 */
  wechatIdentityId: string;
  /** 公众号昵称（实时查询失败时为 null，前端展示「未知昵称」）。 */
  nickname: string | null;
  /** 当前是否已关注公众号（实时校验）。 */
  subscribed: boolean;
  /** 关注时间（ISO 时间戳，未关注或网关未返回时为 null）。 */
  subscribeTime: string | null;
  /** 该微信号最近支付出现时间（ISO 时间戳，辅助员工辨认）。 */
  lastSeenAt: string;
}

/** 催缴收件人查询结果。 */
export interface DunningRecipientsResult {
  /** 监护人显示名（空时前端兜底「家长」）。 */
  guardianName: string | null;
  recipients: DunningRecipientView[];
  /** 无任何已关注微信号时的阻断提示与替代联系方式建议；存在可发送收件人时为 null。 */
  blockedHint: string | null;
}

/** 催缴命令执行结果口径。 */
export type SendDunningOutcome =
  /** 意图已提交（渠道开关关闭时响应携带 skippedBySwitch 标记） */
  | 'SENT'
  /** 同一员工同日已对同节点同收件人催缴过，幂等键拦截（次日可再发） */
  | 'ALREADY_SENT_TODAY'
  /** 收件人当前未关注公众号，阻断发送并返回替代联系方式提示 */
  | 'BLOCKED_UNSUBSCRIBED';

/** 催缴命令结果（前端按 outcome 呈现结果态）。 */
export interface SendDunningResult {
  outcome: SendDunningOutcome;
  /** 意图已提交时是否因公众号通知开关关闭将被跳过。 */
  skippedBySwitch: boolean;
  /** SENT 时的业务消息 ID（其余结果为 null）。 */
  businessMessageId: string | null;
  /** BLOCKED_UNSUBSCRIBED 时的替代联系方式提示文案，其余为 null。 */
  blockedHint: string | null;
}

/** 订单状态展示映射。 */
export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING_PAYMENT: '待支付',
  PARTIALLY_PAID: '部分支付',
  PAID: '已支付',
  CANCELLED: '已取消',
  EXPIRED: '已过期',
};

/** 款项形态展示映射。 */
export const PAYMENT_FORM_LABEL: Record<OrderPaymentForm, string> = {
  UNDETERMINED: '待确定',
  FULL: '全款',
  INSTALLMENT: '分期',
};

/** 应收节点类型展示映射。 */
export const RECEIVABLE_TYPE_LABEL: Record<ReceivableType, string> = {
  DEPOSIT: '首付款',
  FINAL: '尾款',
  FULL: '全款',
};

/** 节点到期状态展示映射。 */
export const RECEIVABLE_DUE_STATUS_LABEL: Record<ReceivableDueStatus, string> = {
  PENDING: '待收款',
  OVERDUE: '已逾期',
  SETTLED: '已结清',
};

/** 切换商品结构化原因选项。 */
export const ORDER_SWITCH_REASON_OPTIONS: Array<{
  value: OrderSwitchReasonCode;
  label: string;
}> = [
  { value: 'SALES_ADJUSTMENT', label: '销售方案调整' },
  { value: 'PARENT_REQUEST', label: '家长要求' },
  { value: 'SCHEDULE_CONFLICT', label: '排课 / 时间冲突' },
  { value: 'PRICE_PLAN_CHANGE', label: '价格方案变化' },
  { value: 'OTHER', label: '其他原因' },
];

/** 管理端展示手机号统一脱敏，避免列表泄漏明文。 */
export function maskOrderMobile(mobile: string | null | undefined): string {
  if (!mobile) return '-';
  const digits = mobile.replace(/\D/g, '');
  const national = digits.length === 13 && digits.startsWith('86') ? digits.slice(2) : digits;
  if (/^1\d{10}$/.test(national)) return `${national.slice(0, 3)}****${national.slice(7)}`;
  return '****';
}

/** 订单筛选参数序列化（列表与导出共用）：剔除空值，统一转字符串。 */
function buildOrderFilterParams(query: OrderListQuery): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params[key] = `${value}`;
    }
  }
  return params;
}

function buildListQuery(query: OrderListQuery): string {
  const qs = new URLSearchParams(buildOrderFilterParams(query)).toString();
  return qs ? `${ApiPaths.orders.list}?${qs}` : ApiPaths.orders.list;
}

/** 收入统计筛选。 */
export interface RevenueStatisticsQuery {
  campusId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** 收入统计汇总（金额为分）。 */
export interface RevenueSummary {
  campusId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  receivableAmountMinor: number;
  paidAmountMinor: number;
  revenueAmountMinor: number;
  unpaidAmountMinor: number;
  reversedAmountMinor: number;
  discountAmountMinor: number;
}

/** SKU 收入明细一行。 */
export interface SkuRevenueBreakdownItem {
  skuId: string;
  skuCode: string;
  skuName: string;
  orderCount: number;
  studentCount: number;
  receivableAmountMinor: number;
  paidAmountMinor: number;
  revenueAmountMinor: number;
  unpaidAmountMinor: number;
  reversedAmountMinor: number;
}

function buildRevenueQuery(query: RevenueStatisticsQuery): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params[key] = `${value}`;
    }
  }
  return params;
}

async function getRevenueSummary(query: RevenueStatisticsQuery = {}): Promise<RevenueSummary> {
  const qs = new URLSearchParams(buildRevenueQuery(query)).toString();
  const path = qs ? `${ApiPaths.revenueStatistics.summary}?${qs}` : ApiPaths.revenueStatistics.summary;
  return http.get<RevenueSummary>(path);
}

async function getSkuRevenueBreakdown(
  query: RevenueStatisticsQuery = {},
): Promise<{ items: SkuRevenueBreakdownItem[] }> {
  const qs = new URLSearchParams(buildRevenueQuery(query)).toString();
  const path = qs
    ? `${ApiPaths.revenueStatistics.skuBreakdown}?${qs}`
    : ApiPaths.revenueStatistics.skuBreakdown;
  return http.get<{ items: SkuRevenueBreakdownItem[] }>(path);
}

async function listOrders(query: OrderListQuery = {}): Promise<Paginated<OrderListItem>> {
  return http.get<Paginated<OrderListItem>>(buildListQuery(query));
}

/** 经临时 `<a download>` 触发浏览器保存文件，完成后立即释放 blob URL。 */
function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** 按当前筛选条件导出全部匹配订单（CSV 附件，服务端脱敏手机号）。 */
async function exportOrders(query: OrderListQuery = {}): Promise<void> {
  const { blob, filename } = await http.download(
    ApiPaths.orders.export,
    buildOrderFilterParams(query),
  );
  triggerBrowserDownload(blob, filename ?? `orders-${Date.now()}.csv`);
}

async function getOrder(id: string): Promise<OrderDetail> {
  return http.get<OrderDetail>(ApiPaths.orders.detail(id));
}

async function createOrder(payload: CreateOrderPayload): Promise<CreateOrderResult> {
  return http.post<CreateOrderResult>(ApiPaths.orders.create, payload);
}

async function createDirectOrder(payload: CreateDirectOrderPayload): Promise<CreateOrderResult> {
  return http.post<CreateOrderResult>(ApiPaths.orders.createDirect, payload);
}

async function cancelOrder(id: string, reason?: string): Promise<{ orderId: string }> {
  return http.post<{ orderId: string }>(ApiPaths.orders.cancel(id), { reason });
}

/** 软删除未付款订单（invalid-order-soft-delete；服务端 409=已付首付/全款）。 */
async function deleteOrder(id: string): Promise<{ orderId: string }> {
  return http.delete<{ orderId: string }>(ApiPaths.orders.softDelete(id));
}

/** 登记退款请求载荷（金额单位分；幂等键同订单内唯一）。 */
/** 退款渠道（线下渠道事实，对齐原型 ADM-33） */
export type RefundChannel = 'WECHAT' | 'ALIPAY' | 'CASH' | 'BANK_TRANSFER' | 'OTHER';

export interface RegisterOrderRefundPayload {
  amountMinor: number;
  reason: string;
  remark?: string | null;
  idempotencyKey: string;
  channel: RefundChannel;
  /** 渠道退款单号/凭证号（非现金渠道必填，现金可不填） */
  channelRefNo?: string | null;
  /** 退款凭证附件对象 key（可选，refund-evidences/ 前缀） */
  evidenceFileKey?: string | null;
}

/** 登记退款结果（含登记后实收口径，分）。 */
export interface RegisterOrderRefundResult {
  refundId: string;
  /** 退款单号（REFUND-YYYYMMDD-随机） */
  refundNo: string;
  channel: string;
  refundedAmountMinor: number;
  netReceivedAmountMinor: number;
}

/** 退款凭证上传凭证签发（refund-evidences/ 独立前缀，直传对象存储）。 */
export interface RefundEvidenceUploadCredentials {
  objectKey: string;
  credentials: {
    secretId: string;
    secretKey: string;
    sessionToken: string;
    allowedPrefix: string;
    expiredAt: string;
  };
  directUpload: boolean;
  filename: string;
}

/** 签发退款凭证上传凭证（UI 方案 v0.7 ADM-33 退款凭证附件）。 */
async function requestRefundEvidenceUpload(
  orderId: string,
  filename: string,
  fileSizeBytes: number,
): Promise<RefundEvidenceUploadCredentials> {
  return http.post<RefundEvidenceUploadCredentials>(
    ApiPaths.orders.refundEvidenceUpload(orderId),
    { filename, fileSizeBytes },
  );
}

/** 登记订单退款（ADM-09 REFUND；同键同载荷幂等重放返回首次结果，异载荷 409）。 */
async function registerOrderRefund(
  orderId: string,
  payload: RegisterOrderRefundPayload,
): Promise<RegisterOrderRefundResult> {
  return http.post<RegisterOrderRefundResult>(ApiPaths.orders.refunds(orderId), payload);
}

async function getOfflinePaymentContext(orderId: string): Promise<OfflinePaymentContext> {
  return http.get<OfflinePaymentContext>(ApiPaths.orders.offlinePaymentContext(orderId));
}

async function recordOfflinePayment(
  orderId: string,
  payload: RecordOfflinePaymentPayload,
  idempotencyKey: string,
): Promise<RecordOfflinePaymentResult> {
  return http.post<RecordOfflinePaymentResult>(
    ApiPaths.orders.recordOfflinePayment(orderId),
    payload,
    { 'Idempotency-Key': idempotencyKey },
  );
}

async function requestOfflinePaymentReceiptUpload(
  orderId: string,
  payload: RequestOfflinePaymentReceiptUploadPayload,
): Promise<RequestOfflinePaymentReceiptUploadResult> {
  return http.post<RequestOfflinePaymentReceiptUploadResult>(
    ApiPaths.orders.offlinePaymentReceiptUploadCredentials(orderId),
    payload,
  );
}

async function accessOfflinePaymentReceipt(
  orderId: string,
  payload: AccessOfflinePaymentReceiptPayload,
): Promise<AccessOfflinePaymentReceiptResult> {
  return http.post<AccessOfflinePaymentReceiptResult>(
    ApiPaths.orders.offlinePaymentReceiptAccess(orderId),
    payload,
  );
}

async function getDunningRecipients(
  orderId: string,
  receivablePlanId: string,
): Promise<DunningRecipientsResult> {
  return http.get<DunningRecipientsResult>(
    ApiPaths.orders.dunningRecipients(orderId, receivablePlanId),
  );
}

async function sendDunning(
  orderId: string,
  receivablePlanId: string,
  wechatIdentityId: string,
): Promise<SendDunningResult> {
  return http.post<SendDunningResult>(
    ApiPaths.orders.sendDunning(orderId, receivablePlanId),
    { wechatIdentityId },
  );
}

/** 订单 API 唯一对外入口。 */
export const billingApi = {
  listOrders,
  exportOrders,
  getOrder,
  createOrder,
  createDirectOrder,
  cancelOrder,
  deleteOrder,
  registerOrderRefund,
  requestRefundEvidenceUpload,
  getOfflinePaymentContext,
  recordOfflinePayment,
  requestOfflinePaymentReceiptUpload,
  accessOfflinePaymentReceipt,
  getDunningRecipients,
  sendDunning,
  getRevenueSummary,
  getSkuRevenueBreakdown,
};
